import { ReactNode, useState } from 'react';
import cn from 'classnames';
import { useFormikContext, FormikContextType, useField } from 'formik';
import Autocomplete from 'react-google-autocomplete';

import Text from '@/components/common/Text/Text';

import './InputField.style.scss';

type AutocompletePlacePart = {
  long_name: string;
  short_name: string;
  types: string[];
};

type AutocompletePlace = {
  address_components: AutocompletePlacePart[];
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  place_id: string;
};

/**
 * Get only the address portion of the given result
 * @param place
 * @returns string
 */
const getAddress = (place: AutocompletePlace): string => {
  if (!place.address_components) return '';

  const number = place.address_components?.find((part) =>
    part.types.includes('street_number')
  );
  const street = place.address_components?.find((part) =>
    part.types.includes('route')
  );

  return `${number?.short_name} ${street?.short_name}`;
};

type InputFieldProps = {
  label: ReactNode;
  className?: string;
  onAddressSelected: (
    formikContext: FormikContextType<unknown>,
    place: string
  ) => void;
  name: string;
  defaultValue?: string;
};

function AddressInputField(props: InputFieldProps) {
  const {
    label,
    className = '',
    onAddressSelected,
    name,
    defaultValue
  } = props;
  const [field, meta /*, helpers */] = useField({ name });
  const formikContext = useFormikContext();
  const [localValue, setLocalValue] = useState<string>(defaultValue || '');

  return (
    <div className={cn('AddressInputField', 'InputField', className)}>
      <label className='AddressInputField InputField-label'>{label}</label>
      <Autocomplete
        className={cn('InputField-input')}
        // @ts-ignore
        value={localValue}
        apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
        // This listener is updates the field upon Autocomplete selection
        onPlaceSelected={(place) => {
          if (place?.formatted_address) {
            onAddressSelected(formikContext, place.formatted_address);

            // Set the field value to be the properly formatted
            // address (street no + name) instead of the entire address
            const address = getAddress(place);
            formikContext.setFieldValue(name, address);
            setLocalValue(address);
          }
        }}
        // This listener updates the field per traditional user input/keystroke
        onChange={(event) => {
          const value = event.currentTarget.value;
          formikContext.setFieldValue(name, value);
          setLocalValue(value);
        }}
        // Note: Might be worth making these top level props for better visibility
        options={{
          types: ['street_number', 'street_address'],
          componentRestrictions: { country: 'us' },
          name
        }}
      />
      {meta.touched && meta.error && (
        <div className='InputField-error'>
          <Text className='InputField-error-text'>{meta.error}</Text>
        </div>
      )}
    </div>
  );
}

export default AddressInputField;
